import { useCallback, useMemo } from "react";

import { isDrawingFormCategory } from "@simplyk/common";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { useAmplitude } from "../../../../../common/hooks/amplitude/useAmplitude";
import { useFormInputChanged } from "../../../../../common/hooks/useFormInputChanged";
import { useIsOnboarding } from "../../../../../common/hooks/useIsOnboarding";
import { useStableLiveFormEditorContext } from "../LiveFormEditorContext";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

interface LiveFormAmplitudeEvents {
  liveFormAmplitudePayload: Record<string, unknown>;
  logUserActionEvent: (event: AmplitudeEvents, properties?: Record<string, unknown>) => void;
  logUserInputEvent: ({
    inputName,
    isEmpty,
    isValid,
  }: {
    inputName: string;
    isEmpty: boolean;
    isValid: boolean;
  }) => () => void;
}

export const useLiveFormAmplitudeEvents = (): LiveFormAmplitudeEvents => {
  const { editOrCreateMode, currentStepKey, formCategory, apiFormObject } = useStableLiveFormEditorContext();
  const { isOnboarding } = useIsOnboarding();

  const { logAmplitudeEvent } = useAmplitude();

  const { formInputChangedCallback } = useFormInputChanged();
  const isSweepstakes = isSweepstakesCategory(formCategory);

  const liveFormAmplitudePayload = useMemo(
    () => ({
      form_id: apiFormObject.id,
      form_type: formCategory.toLowerCase(),
      step: currentStepKey,
      is_new_form: editOrCreateMode === "create",
      is_onboarding: isOnboarding,
      is_liveform: true,
      is_sweepstakes: isSweepstakes,
      ...(isDrawingFormCategory(formCategory) && {
        lottery_type: isSweepstakes ? "sweepstakes" : "raffle",
      }),
    }),
    [apiFormObject.id, formCategory, currentStepKey, editOrCreateMode, isOnboarding, isSweepstakes]
  );

  const logUserActionEvent = useCallback(
    (event: AmplitudeEvents, properties?: Record<string, unknown>) => {
      logAmplitudeEvent(event, { ...liveFormAmplitudePayload, ...properties });
    },
    [logAmplitudeEvent, liveFormAmplitudePayload]
  );

  const logUserInputEvent = useCallback(
    ({ inputName, isEmpty, isValid }: { inputName: string; isEmpty: boolean; isValid: boolean }) => {
      return formInputChangedCallback({
        input_name: inputName,
        field_empty: isEmpty,
        valid_field: isValid,
        ...liveFormAmplitudePayload,
      });
    },
    [formInputChangedCallback, liveFormAmplitudePayload]
  );

  return {
    liveFormAmplitudePayload,
    logUserActionEvent,
    logUserInputEvent,
  };
};
